import * as React from "react";
import {
  AtSign,
  ClipboardList,
  ContactRound,
  Globe,
  Link as LinkIcon,
  Link2,
  Mail,
  MessageSquareText,
  PenLine,
  Phone,
  Share2,
  UserRound,
} from "lucide-react";
import { CTA, CTAType, Player } from "@/services/bedrock";
import { useAccountSettings } from "@/utils/useAccountSettings";
import { usePlayerSettings } from "@/utils/usePlayerSettings";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { CoachSettings, WebSettings } from "@/interfaces";

export const CtaTypeGroupItems = ["Links", "Call", "Text", "Email"] as const;
export type CtaTypeGroup = (typeof CtaTypeGroupItems)[number];

export type CtaTypeGroupDefinition = {
  icon: React.ReactNode;
  description: string;
};

export const CtaTypeGroupDefinitions: Record<CtaTypeGroup, CtaTypeGroupDefinition> = {
  Links: {
    icon: <LinkIcon className="size-5" />,
    description: "Open a link",
  },
  Call: {
    icon: <Phone className="size-5" />,
    description: "Start a phone call",
  },
  Text: {
    icon: <MessageSquareText className="size-5" />,
    description: "Start a text message",
  },
  Email: {
    icon: <Mail className="size-5" />,
    description: "Start an email",
  },
};

type CtaInputKind = "link" | "phone" | "email" | "form" | "smartLink" | "resolved";

export type CtaTypeDefinition = {
  group: CtaTypeGroup;
  icon: React.ReactNode;
  title: (isPlayer: boolean) => string;
  description: (isPlayer: boolean) => string;
  input: CtaInputKind;
  defaultLabel: string;
  prefix?: string;
  placeholder?: string;
  validate?: (value: string | null) => true | string;
  resolvePreview?: (player: Player) => string | null | undefined;
  missingValueWarning?: (isPlayer: boolean) => string;
};

const validateLink = (value: string | null): true | string =>
  !/^(https:\/\/)?[\w-]+(\.[\w-]{2,})+([/?#][^\s]*)?$/i.test(value ?? "")
    ? "Please enter a valid URL (e.g. example.com)"
    : true;

export const formatCTAPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 10) return value;
  return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
};

const validatePhone = (value: string | null): true | string => {
  const digits = (value ?? "").replace(/\D/g, "");
  return digits.length === 10 ? true : "Please enter a valid 10-digit phone number";
};

const validateEmail = (value: string | null): true | string =>
  !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(value ?? "") ? "Please enter a valid email address" : true;

const whose = (isPlayer: boolean) => (isPlayer ? "your" : "the Player's");

export const CtaTypeDefinitions: Record<CTAType, CtaTypeDefinition> = {
  custom_url: {
    group: "Links",
    icon: <LinkIcon className="size-4" />,
    title: () => "Custom URL",
    description: () => "Sends visitors to any URL you enter.",
    input: "link",
    defaultLabel: "Learn More",
    prefix: "https://",
    placeholder: "example.com",
    validate: validateLink,
  },
  player_short_link: {
    group: "Links",
    icon: <Link2 className="size-4" />,
    title: isPlayer => (isPlayer ? "My Short Link" : "Player Short Link"),
    description: isPlayer => `Sends visitors to ${whose(isPlayer)} short link destination.`,
    input: "resolved",
    defaultLabel: "Learn More",
    prefix: "https://",
    resolvePreview: player => player.shortLink?.url,
    missingValueWarning: isPlayer =>
      isPlayer
        ? "You don't have a short link set up, so the button will be hidden until one is created."
        : "This Player doesn't have a short link set up, so the button will be hidden until one is created.",
  },
  player_website: {
    group: "Links",
    icon: <Globe className="size-4" />,
    title: isPlayer => (isPlayer ? "My Website" : "Player Website"),
    description: isPlayer => `Sends visitors to ${whose(isPlayer)} website.`,
    input: "resolved",
    defaultLabel: "Visit My Website",
    prefix: "https://",
    resolvePreview: player => player.webAddress,
    missingValueWarning: isPlayer =>
      isPlayer
        ? "You don't have a website on file, so the button will be hidden until one is added."
        : "This Player doesn't have a website on file, so the button will be hidden until one is added.",
  },
  player_card: {
    group: "Links",
    icon: <ContactRound className="size-4" />,
    title: isPlayer => (isPlayer ? "My Player Card" : "Player Card"),
    description: isPlayer => `Opens ${whose(isPlayer)} Player Card page.`,
    input: "resolved",
    defaultLabel: "View My Card",
    prefix: "https://",
    resolvePreview: player => (player.hasPlayerCard ? player.playerCardUrl : null),
    missingValueWarning: isPlayer =>
      isPlayer
        ? "Your Player Card isn't published yet, so the button will be hidden until it is."
        : "This Player's card isn't published yet, so the button will be hidden until it is.",
  },
  form_link: {
    group: "Links",
    icon: <ClipboardList className="size-4" />,
    title: () => "Form Link",
    description: () => "Sends visitors to one of your feedback forms.",
    input: "form",
    defaultLabel: "Share Your Feedback",
  },
  smart_link: {
    group: "Links",
    icon: <Share2 className="size-4" />,
    title: () => "Smart Link",
    description: isPlayer =>
      isPlayer
        ? "Sends visitors through a Smart Link with click tracking and your attribution."
        : "Sends visitors through a Smart Link with click tracking and Player attribution.",
    input: "smartLink",
    defaultLabel: "Check This Out",
  },
  player_phone: {
    group: "Call",
    icon: <UserRound className="size-4" />,
    title: isPlayer => (isPlayer ? "Call Me" : "Call Player"),
    description: isPlayer => `Starts a phone call to ${whose(isPlayer)} phone number.`,
    input: "resolved",
    defaultLabel: "Call Me",
    prefix: "tel:",
    resolvePreview: player => player.phone,
    missingValueWarning: isPlayer =>
      isPlayer
        ? "You don't have a phone number on file, so the button will be hidden until one is added."
        : "This Player doesn't have a phone number on file, so the button will be hidden until one is added.",
  },
  custom_phone: {
    group: "Call",
    icon: <PenLine className="size-4" />,
    title: () => "Custom Number",
    description: () => "Starts a phone call to a number you enter.",
    input: "phone",
    defaultLabel: "Call Us",
    prefix: "tel:",
    placeholder: "555-555-5555",
    validate: validatePhone,
  },
  player_sms: {
    group: "Text",
    icon: <UserRound className="size-4" />,
    title: isPlayer => (isPlayer ? "Text Me" : "Text Player"),
    description: isPlayer => `Starts a text message to ${whose(isPlayer)} phone number.`,
    input: "resolved",
    defaultLabel: "Text Me",
    prefix: "sms:",
    resolvePreview: player => player.phone,
    missingValueWarning: isPlayer =>
      isPlayer
        ? "You don't have a phone number on file, so the button will be hidden until one is added."
        : "This Player doesn't have a phone number on file, so the button will be hidden until one is added.",
  },
  custom_sms: {
    group: "Text",
    icon: <PenLine className="size-4" />,
    title: () => "Custom Number",
    description: () => "Starts a text message to a number you enter.",
    input: "phone",
    defaultLabel: "Text Us",
    prefix: "sms:",
    placeholder: "555-555-5555",
    validate: validatePhone,
  },
  player_email: {
    group: "Email",
    icon: <UserRound className="size-4" />,
    title: isPlayer => (isPlayer ? "Email Me" : "Email Player"),
    description: isPlayer => `Starts an email to ${whose(isPlayer)} email address.`,
    input: "resolved",
    defaultLabel: "Email Me",
    prefix: "mailto:",
    resolvePreview: player => player.email,
    missingValueWarning: isPlayer =>
      isPlayer
        ? "You don't have an email address on file, so the button will be hidden until one is added."
        : "This Player doesn't have an email address on file, so the button will be hidden until one is added.",
  },
  custom_email: {
    group: "Email",
    icon: <AtSign className="size-4" />,
    title: () => "Custom Email",
    description: () => "Starts an email to an address you enter.",
    input: "email",
    defaultLabel: "Email Us",
    prefix: "mailto:",
    placeholder: "example@example.com",
    validate: validateEmail,
  },
};

export const useCtaTypeGating = () => {
  const { hasAccountSetting } = useAccountSettings();
  const { isPlayer, playerId } = useSocialCoachSession();
  const { hasSetting } = usePlayerSettings(isPlayer ? playerId : undefined);

  const isTypeAvailable = React.useCallback(
    (type: CTAType): boolean => {
      switch (type) {
        case "smart_link":
          return hasAccountSetting(WebSettings.LINK_TRACKING);
        case "form_link":
          return hasAccountSetting(WebSettings.REPUTATION_MANAGEMENT);
        case "player_card":
          if (!hasAccountSetting(WebSettings.ALLOW_ACCESS_TO_PLAYER_CARDS)) return false;
          if (isPlayer) return hasSetting(CoachSettings.ALLOW_PLAYER_ACCESS_TO_PLAYER_CARDS) ?? false;
          return true;
        default:
          return true;
      }
    },
    [hasAccountSetting, hasSetting, isPlayer]
  );

  return { isTypeAvailable };
};

export const describeCta = (cta: CTA, isPlayer: boolean): string => {
  const definition = CtaTypeDefinitions[cta.type];
  if (!definition) return cta.label;
  const title = definition.title(isPlayer);
  return cta.destinationLabel ? `${title} — ${cta.destinationLabel}` : title;
};
